import React, { useEffect, useMemo, useState } from "react";

/* -------------------- constants -------------------- */
const MACRO_CANDIDATE_URLS = [
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/macro_dimension_scores.json",
  "https://cdn.jsdelivr.net/gh/aport550/majorah-data@main/public/data/macro_dimension_scores.json",
];

const DAILY_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

const MACRO_ENVIRONMENT_ORDER = [
  "Inflationary Growth + Easy Liquidity",
  "Inflationary Growth",
  "Stagflation + Easy Liquidity",
  "Stagflation",
  "Deflationary Growth",
  "Deflationary Growth + Tight Liquidity",
  "Deflationary Recession",
  "Deflationary Recession + Tight Liquidity",
];

/* -------------------- helpers -------------------- */
function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function normalizeDateString(value) {
  const s = String(value || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return s;

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function parseReturnValue(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return num / 100;
}

function compareValues(a, b, direction = "asc") {
  const dir = direction === "asc" ? 1 : -1;

  const aMissing =
    a === null || a === undefined || a === "" || Number.isNaN(a);
  const bMissing =
    b === null || b === undefined || b === "" || Number.isNaN(b);

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * dir;
  }

  return String(a).localeCompare(String(b)) * dir;
}

function formatSignedPercentFromDecimal(v, decimals = 2) {
  if (!isFiniteNumber(v)) return "—";
  const pct = `${(v * 100).toFixed(decimals)}%`;
  return v > 0 ? `+${pct}` : pct;
}

function regimeToKey(regime) {
  return String(regime || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function parseDailyReturnsCsvAllTickers(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (!lines.length) {
    return { byDate: new Map(), tickers: [] };
  }

  const header = parseCsvLine(lines[0]);

  let dateIdx = header.findIndex(
    (h) => String(h).trim().toLowerCase() === "date"
  );

  if (dateIdx === -1 && header.length > 0) {
    const firstHeader = String(header[0] || "").trim().toLowerCase();
    if (firstHeader === "" || firstHeader === "unnamed: 0") {
      dateIdx = 0;
    }
  }

  if (dateIdx === -1) {
    throw new Error("daily_returns.csv must contain a date column.");
  }

  const symbolColumns = [];
  for (let i = 0; i < header.length; i++) {
    if (i === dateIdx) continue;
    const symbol = String(header[i] || "").trim().toUpperCase();
    if (!symbol) continue;
    symbolColumns.push({ idx: i, symbol });
  }

  const byDate = new Map();

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const date = normalizeDateString(cols[dateIdx]);
    if (!date) continue;

    const row = {};
    for (const col of symbolColumns) {
      row[col.symbol] = parseReturnValue(cols[col.idx]);
    }
    byDate.set(date, row);
  }

  return {
    byDate,
    tickers: symbolColumns.map((c) => c.symbol),
  };
}

async function fetchText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} from ${url}`);
  }
  return res.text();
}

async function fetchJsonFromCandidates(urls) {
  let lastError = null;

  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: "no-store" });

      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status} from ${url}`);
        continue;
      }

      const text = await res.text();
      const trimmed = text.trim();

      if (!trimmed) {
        lastError = new Error(`Empty response from ${url}`);
        continue;
      }

      if (
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<html") ||
        trimmed.startsWith("<")
      ) {
        lastError = new Error(`Expected JSON but received HTML from ${url}`);
        continue;
      }

      const json = JSON.parse(text);
      return { json, url };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Unable to load JSON.");
}

/* -------------------- macro helpers -------------------- */
function getSignLabel(v) {
  if (!isFiniteNumber(v)) return null;
  return v >= 0 ? "pos" : "neg";
}

function getMacroRegime(row) {
  const inf = getSignLabel(row?.inflation_score);
  const gro = getSignLabel(row?.growth_score);
  const liq = getSignLabel(row?.liquidity_score);

  if (!inf || !gro || !liq) return "—";

  const key = `${inf}_${gro}_${liq}`;

  const regimeMap = {
    pos_pos_pos: "Inflationary Growth + Easy Liquidity",
    pos_pos_neg: "Inflationary Growth",
    pos_neg_pos: "Stagflation + Easy Liquidity",
    pos_neg_neg: "Stagflation",
    neg_pos_pos: "Deflationary Growth",
    neg_pos_neg: "Deflationary Growth + Tight Liquidity",
    neg_neg_pos: "Deflationary Recession",
    neg_neg_neg: "Deflationary Recession + Tight Liquidity",
  };

  return regimeMap[key] || "—";
}

function getReturnStyle(v) {
  if (!isFiniteNumber(v)) return { color: "#6b7280" };
  if (v > 0) return { color: "#15803d", fontWeight: 700 };
  if (v < 0) return { color: "#b91c1c", fontWeight: 700 };
  return { color: "#374151", fontWeight: 600 };
}

function getMacroStockType(row) {
  let bestRegime = null;
  let bestValue = -Infinity;

  for (const regime of MACRO_ENVIRONMENT_ORDER) {
    const key = regimeToKey(regime);
    const value = row?.[`${key}__avg_return_vs_spy`];
    if (isFiniteNumber(value) && value > bestValue) {
      bestValue = value;
      bestRegime = regime;
    }
  }

  return bestRegime || "—";
}

/* -------------------- component -------------------- */
export default function MacroComparisonTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [sort, setSort] = useState({
    key: "stock",
    direction: "asc",
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        setRows([]);

        const [{ json: macroJson }, dailyReturnsText] = await Promise.all([
          fetchJsonFromCandidates(MACRO_CANDIDATE_URLS),
          fetchText(DAILY_RETURNS_URL),
        ]);

        if (!Array.isArray(macroJson)) {
          throw new Error("Macro dimension JSON is not an array.");
        }

        const allReturnsParsed = parseDailyReturnsCsvAllTickers(dailyReturnsText);
        const tickerUniverse = allReturnsParsed.tickers.filter(
          (t) => t && t !== "SPY"
        );

        const stockAggs = new Map();

        for (const ticker of tickerUniverse) {
          const regimeAggs = new Map();
          for (const regime of MACRO_ENVIRONMENT_ORDER) {
            regimeAggs.set(regime, {
              sum_relative: 0,
              count_relative: 0,
            });
          }
          stockAggs.set(ticker, regimeAggs);
        }

        for (const m of macroJson) {
          const date = normalizeDateString(m?.date);
          if (!date) continue;

          const inflation_score = Number.isFinite(Number(m?.inflation_score))
            ? Number(m.inflation_score)
            : null;
          const growth_score = Number.isFinite(Number(m?.growth_score))
            ? Number(m.growth_score)
            : null;
          const liquidity_score = Number.isFinite(Number(m?.liquidity_score))
            ? Number(m.liquidity_score)
            : null;

          const regime = getMacroRegime({
            inflation_score,
            growth_score,
            liquidity_score,
          });

          if (!regime || regime === "—") continue;

          const dayReturns = allReturnsParsed.byDate.get(date);
          if (!dayReturns) continue;

          const spyReturn = dayReturns.SPY ?? null;
          if (!isFiniteNumber(spyReturn)) continue;

          for (const ticker of tickerUniverse) {
            const stockReturn = dayReturns[ticker];
            if (!isFiniteNumber(stockReturn)) continue;

            const agg = stockAggs.get(ticker)?.get(regime);
            if (!agg) continue;

            agg.sum_relative += stockReturn - spyReturn;
            agg.count_relative += 1;
          }
        }

        const allRows = tickerUniverse.map((ticker) => {
          const regimeAggs = stockAggs.get(ticker);
          const row = { stock: ticker };

          for (const regime of MACRO_ENVIRONMENT_ORDER) {
            const key = regimeToKey(regime);
            const agg = regimeAggs.get(regime);

            const avgVsSpy =
              agg.count_relative > 0
                ? agg.sum_relative / agg.count_relative
                : null;

            row[`${key}__avg_return_vs_spy`] = avgVsSpy;
          }

          row.macro_stock_type = getMacroStockType(row);

          return row;
        });

        if (!cancelled) {
          setRows(allRows);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load macro comparison table.");
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  function toggleSort(key) {
    setSort((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        direction:
          key === "stock" || key === "macro_stock_type" ? "asc" : "desc",
      };
    });
  }

  function renderSortIndicator(key) {
    if (sort.key !== key) return " ↕";
    return sort.direction === "asc" ? " ↑" : " ↓";
  }

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toUpperCase();
    if (!q) return rows;

    return rows.filter((row) => {
      const stock = String(row.stock || "").toUpperCase();
      const macroType = String(row.macro_stock_type || "").toUpperCase();
      return stock.includes(q) || macroType.includes(q);
    });
  }, [rows, searchTerm]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    const { key, direction } = sort;

    arr.sort((a, b) => {
      const aVal = a?.[key];
      const bVal = b?.[key];

      if (typeof aVal === "string" || typeof bVal === "string") {
        return compareValues(String(aVal ?? ""), String(bVal ?? ""), direction);
      }

      return compareValues(aVal, bVal, direction);
    });

    return arr;
  }, [filteredRows, sort]);

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1550,
        margin: "0 auto",
        padding: 12,
        fontFamily:
          "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <div
          style={{
            padding: "10px 12px",
            background: "#fafafa",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              fontWeight: 800,
              fontSize: 15,
              marginBottom: 10,
            }}
          >
            Macro Comparison Table
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                position: "relative",
                width: 240,
                maxWidth: "100%",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6b7280",
                  fontSize: 13,
                  pointerEvents: "none",
                }}
              >
                🔍
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search ticker or macro type..."
                style={{
                  width: "100%",
                  padding: "8px 10px 8px 30px",
                  border: "1px solid #d1d5db",
                  borderRadius: 9999,
                  fontSize: 13,
                  outline: "none",
                  background: "#fff",
                }}
              />
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              Showing {sortedRows.length} of {rows.length} stocks
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 16, color: "#666" }}>Loading macro comparison…</div>
        ) : error ? (
          <div
            style={{
              padding: 16,
              color: "#b91c1c",
              background: "#fef2f2",
              borderTop: "1px solid #fecaca",
            }}
          >
            {error}
          </div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: 16, color: "#666" }}>
            No macro comparison data available.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
              }}
            >
              <colgroup>
                <col style={{ width: "90px" }} />
                <col style={{ width: "180px" }} />
                {MACRO_ENVIRONMENT_ORDER.map((regime) => (
                  <col key={regime} style={{ width: "130px" }} />
                ))}
              </colgroup>

              <thead
                style={{
                  background: "#f9fafb",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>
                  <th
                    style={{
                      ...tableThButtonStyle,
                      position: "sticky",
                      left: 0,
                      zIndex: 4,
                      background: "#f9fafb",
                    }}
                    onClick={() => toggleSort("stock")}
                    title="Sort by stock"
                  >
                    <div style={headerTextWrapStyle}>
                      Stock{renderSortIndicator("stock")}
                    </div>
                  </th>

                  <th
                    style={{
                      ...tableThButtonStyle,
                      position: "sticky",
                      left: 90,
                      zIndex: 4,
                      background: "#f9fafb",
                    }}
                    onClick={() => toggleSort("macro_stock_type")}
                    title="Sort by macro stock type"
                  >
                    <div style={headerTextWrapStyle}>
                      Macro Stock Type{renderSortIndicator("macro_stock_type")}
                    </div>
                  </th>

                  {MACRO_ENVIRONMENT_ORDER.map((regime) => {
                    const key = regimeToKey(regime);
                    return (
                      <th
                        key={regime}
                        style={tableThButtonStyle}
                        onClick={() => toggleSort(`${key}__avg_return_vs_spy`)}
                        title={`${regime} - Avg Daily Return vs SPY`}
                      >
                        <div style={headerTextWrapStyle}>
                          {regime}
                          {renderSortIndicator(`${key}__avg_return_vs_spy`)}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.stock}>
                    <td
                      style={{
                        ...tableTdStyle,
                        fontWeight: 700,
                        position: "sticky",
                        left: 0,
                        background: "#fff",
                        zIndex: 3,
                      }}
                    >
                      {row.stock}
                    </td>

                    <td
                      style={{
                        ...tableTdStyle,
                        position: "sticky",
                        left: 90,
                        background: "#fff",
                        zIndex: 3,
                        fontWeight: 600,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        textAlign: "left",
                      }}
                    >
                      {row.macro_stock_type}
                    </td>

                    {MACRO_ENVIRONMENT_ORDER.map((regime) => {
                      const key = regimeToKey(regime);
                      const avgVsSpy = row[`${key}__avg_return_vs_spy`];

                      return (
                        <td
                          key={`${row.stock}-${regime}`}
                          style={{
                            ...tableTdStyle,
                            ...getReturnStyle(avgVsSpy),
                          }}
                        >
                          {formatSignedPercentFromDecimal(avgVsSpy)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const headerTextWrapStyle = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  lineHeight: 1.15,
};

const tableThStyle = {
  textAlign: "center",
  padding: "8px 6px",
  fontSize: 11,
  color: "#374151",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "1px solid #eef2f7",
  verticalAlign: "middle",
  minWidth: 0,
};

const tableThButtonStyle = {
  ...tableThStyle,
  cursor: "pointer",
  userSelect: "none",
};

const tableTdStyle = {
  padding: "8px 6px",
  borderBottom: "1px solid #f3f4f6",
  borderRight: "1px solid #f8fafc",
  fontSize: 12,
  whiteSpace: "nowrap",
  textAlign: "center",
  verticalAlign: "middle",
};